import { CHARACTERS, CHALLENGES, MODES, WORLDS, challengeById, type Challenge, type ModeId, type WorldId } from './catalog';

const STORAGE_KEY = 'lost-storybook-arcade-save-v1';
const BACKUP_FORMAT = 'lost-storybook-arcade-save';

export interface SaveData {
  version: 3;
  pages: number;
  unlockedCharacters: string[];
  unlockedChallenges: string[];
  selectedCharacters: Partial<Record<WorldId, string>>;
  completedChallenges: string[];
  highScores: Record<string, number>;
  achievements: string[];
  playedModes: ModeId[];
  totalRuns: number;
  totalPagesFound: number;
  soundOn: boolean;
  dailyCompletedDate: string | null;
  dailyStreak: number;
}

export const ACHIEVEMENTS = [
  { id: 'first-flight', title: 'The First Page', detail: 'Finish your first arcade run.', mark: '✦' },
  { id: 'four-modes', title: 'Arcade Adventurer', detail: 'Try all four arcade modes.', mark: '⌘' },
  { id: 'first-story', title: 'Chapter One', detail: 'Complete a story challenge.', mark: '❖' },
  { id: 'three-worlds', title: 'Shelf Explorer', detail: 'Complete challenges in three worlds.', mark: '⌂' },
  { id: 'page-collector', title: 'Page Keeper', detail: 'Collect 100 magical pages.', mark: '✧' },
  { id: 'ten-runs', title: 'A Regular Reader', detail: 'Finish 10 arcade runs.', mark: '☼' },
  { id: 'daily-reader', title: 'The Daily Chapter', detail: 'Complete daily pages three days in a row.', mark: '☾' },
  { id: 'page-hoarder', title: 'Keeper of the Shelves', detail: 'Find 250 pages during play.', mark: '❖' },
];

export function freshSave(): SaveData {
  return {
    version: 3,
    pages: 36,
    unlockedCharacters: CHARACTERS.filter((character) => character.starter).map((character) => character.id),
    unlockedChallenges: [],
    selectedCharacters: Object.fromEntries(CHARACTERS.filter((character) => character.starter).map((character) => [character.worldId, character.id])),
    completedChallenges: [],
    highScores: {},
    achievements: [],
    playedModes: [],
    totalRuns: 0,
    totalPagesFound: 0,
    soundOn: true,
    dailyCompletedDate: null,
    dailyStreak: 0,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function validStringList(value: unknown, allowed: Set<string>): string[] {
  return Array.isArray(value) ? [...new Set(value.filter((item): item is string => typeof item === 'string' && allowed.has(item)))] : [];
}

function nonNegativeInteger(value: unknown, fallback: number): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? Math.min(Number.MAX_SAFE_INTEGER, Math.floor(numeric)) : fallback;
}

/** Convert a local save or exported backup into a safe, current save shape. */
export function normalizeSave(input: unknown): SaveData | null {
  if (!isRecord(input)) return null;
  const payload = input.format === BACKUP_FORMAT ? input.save : input;
  if (!isRecord(payload)) return null;
  const hasSaveMarker = ['unlockedCharacters', 'completedChallenges', 'totalRuns', 'selectedCharacters', 'playedModes'].some((key) => Object.hasOwn(payload, key));
  if (!Object.hasOwn(payload, 'pages') || !hasSaveMarker) return null;
  const sourceVersion = Number(payload.version ?? input.version ?? 1);
  if (!Number.isFinite(sourceVersion) || sourceVersion > 3 || sourceVersion < 1) return null;

  const defaults = freshSave();
  const characterIds = new Set(CHARACTERS.map((item) => item.id));
  const challengeIds = new Set(CHALLENGES.map((item) => item.id));
  const achievementIds = new Set(ACHIEVEMENTS.map((item) => item.id));
  const modeIds = new Set(MODES.map((item) => item.id));
  const unlockedCharacters = new Set(validStringList(payload.unlockedCharacters, characterIds));
  for (const character of CHARACTERS) if (character.starter) unlockedCharacters.add(character.id);

  const selectedCharacters: Partial<Record<WorldId, string>> = { ...defaults.selectedCharacters };
  if (isRecord(payload.selectedCharacters)) {
    for (const world of WORLDS) {
      const characterId = payload.selectedCharacters[world.id];
      if (typeof characterId === 'string' && characterId.length && unlockedCharacters.has(characterId) && CHARACTERS.some((item) => item.id === characterId && item.worldId === world.id)) {
        selectedCharacters[world.id] = characterId;
      }
    }
  }

  const highScores: Record<string, number> = {};
  if (isRecord(payload.highScores)) {
    const allowedScoreKeys = new Set(MODES.flatMap((mode) => WORLDS.map((world) => `${mode.id}:${world.id}`)));
    for (const [key, value] of Object.entries(payload.highScores)) {
      const score = Number(value);
      if (allowedScoreKeys.has(key) && Number.isFinite(score) && score >= 0) highScores[key] = Math.floor(score);
    }
  }

  const dailyDate = typeof payload.dailyCompletedDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(payload.dailyCompletedDate) ? payload.dailyCompletedDate : null;
  return {
    ...defaults,
    version: 3,
    pages: nonNegativeInteger(payload.pages, defaults.pages),
    unlockedCharacters: [...unlockedCharacters],
    unlockedChallenges: validStringList(payload.unlockedChallenges, challengeIds),
    selectedCharacters,
    completedChallenges: validStringList(payload.completedChallenges, challengeIds),
    highScores,
    achievements: validStringList(payload.achievements, achievementIds),
    playedModes: validStringList(payload.playedModes, modeIds) as ModeId[],
    totalRuns: nonNegativeInteger(payload.totalRuns, defaults.totalRuns),
    totalPagesFound: nonNegativeInteger(payload.totalPagesFound, defaults.totalPagesFound),
    soundOn: typeof payload.soundOn === 'boolean' ? payload.soundOn : defaults.soundOn,
    dailyCompletedDate: dailyDate,
    dailyStreak: nonNegativeInteger(payload.dailyStreak, defaults.dailyStreak),
  };
}

export function createSaveBackup(save: SaveData): string {
  return JSON.stringify({ format: BACKUP_FORMAT, version: 1, save: normalizeSave(save) ?? freshSave() }, null, 2);
}

export function parseSaveBackup(contents: string): SaveData | null {
  try {
    return normalizeSave(JSON.parse(contents));
  } catch {
    return null;
  }
}

export function loadSave(): SaveData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return freshSave();
    return normalizeSave(JSON.parse(raw)) ?? freshSave();
  } catch {
    return freshSave();
  }
}

export function persistSave(save: SaveData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(save));
}

export function selectedCharacter(save: SaveData, worldId: WorldId): string {
  const preferred = save.selectedCharacters[worldId];
  if (preferred && save.unlockedCharacters.includes(preferred)) return preferred;
  return CHARACTERS.find((character) => character.worldId === worldId && save.unlockedCharacters.includes(character.id))?.id ?? 'alice';
}

export function unlockCharacter(save: SaveData, characterId: string): boolean {
  const character = CHARACTERS.find((item) => item.id === characterId);
  if (!character || save.unlockedCharacters.includes(characterId) || save.pages < character.unlockCost) return false;
  save.pages -= character.unlockCost;
  save.unlockedCharacters.push(characterId);
  save.selectedCharacters[character.worldId] = characterId;
  persistSave(save);
  return true;
}

export function isChallengeUnlocked(save: SaveData, challenge: Challenge): boolean {
  if (challenge.chapterNumber === 1 || save.unlockedChallenges.includes(challenge.id) || save.completedChallenges.includes(challenge.id)) return true;
  return false;
}

export function canUnlockChallenge(save: SaveData, challenge: Challenge): boolean {
  if (isChallengeUnlocked(save, challenge)) return false;
  const previous = CHALLENGES.find((item) => item.worldId === challenge.worldId && item.mode === challenge.mode && item.chapterNumber === challenge.chapterNumber - 1);
  return Boolean(previous && save.completedChallenges.includes(previous.id) && save.pages >= challenge.unlockCost);
}

export function unlockChallenge(save: SaveData, challengeId: string): boolean {
  const challenge = challengeById(challengeId);
  if (!challenge || isChallengeUnlocked(save, challenge)) return false;
  const previous = CHALLENGES.find((item) => item.worldId === challenge.worldId && item.mode === challenge.mode && item.chapterNumber === challenge.chapterNumber - 1);
  if (!previous || !save.completedChallenges.includes(previous.id) || save.pages < challenge.unlockCost) return false;
  save.pages -= challenge.unlockCost;
  save.unlockedChallenges.push(challenge.id);
  persistSave(save);
  return true;
}

export function selectCharacter(save: SaveData, worldId: WorldId, characterId: string): boolean {
  const character = CHARACTERS.find((item) => item.id === characterId && item.worldId === worldId);
  if (!character || !save.unlockedCharacters.includes(characterId)) return false;
  save.selectedCharacters[worldId] = characterId;
  persistSave(save);
  return true;
}

export interface RunResult {
  mode: ModeId;
  worldId: WorldId;
  score: number;
  pagesFound: number;
  challengeId?: string;
  won: boolean;
  isDaily?: boolean;
  date?: string;
}

function previousDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return '';
  const previous = new Date(Date.UTC(year, month - 1, day) - 86_400_000);
  return `${previous.getUTCFullYear()}-${String(previous.getUTCMonth() + 1).padStart(2, '0')}-${String(previous.getUTCDate()).padStart(2, '0')}`;
}

export function dailyStreakReward(streak: number): number {
  return Math.min(12, Math.max(1, Math.floor(streak)) * 2);
}

export function recordRun(save: SaveData, result: RunResult): { awarded: number; newlyEarned: string[]; firstClear: boolean; dailyStreakBonus: number } {
  save.totalRuns += 1;
  save.totalPagesFound += result.pagesFound;
  save.playedModes = [...new Set([...save.playedModes, result.mode])];
  const scoreKey = `${result.mode}:${result.worldId}`;
  save.highScores[scoreKey] = Math.max(save.highScores[scoreKey] ?? 0, Math.floor(result.score));
  const firstStoryClear = result.won && result.challengeId && !result.isDaily && !save.completedChallenges.includes(result.challengeId);
  if (firstStoryClear && result.challengeId) {
    save.completedChallenges.push(result.challengeId);
  }
  const challengeReward = firstStoryClear && result.challengeId ? (challengeById(result.challengeId)?.reward ?? 0) : 0;
  let dailyStreakBonus = 0;
  if (result.won && result.isDaily) {
    const completionDate = result.date ?? new Date().toISOString().slice(0, 10);
    if (save.dailyCompletedDate !== completionDate) {
      save.dailyStreak = save.dailyCompletedDate === previousDate(completionDate) ? save.dailyStreak + 1 : 1;
      save.dailyCompletedDate = completionDate;
      dailyStreakBonus = dailyStreakReward(save.dailyStreak);
    }
  }
  const award = result.pagesFound + challengeReward + dailyStreakBonus;
  save.pages += award;

  const completedWorlds = new Set(save.completedChallenges.map((id) => challengeById(id)?.worldId).filter(Boolean));
  const checks: Record<string, boolean> = {
    'first-flight': save.totalRuns > 0,
    'four-modes': save.playedModes.length >= 4,
    'first-story': save.completedChallenges.length > 0,
    'three-worlds': completedWorlds.size >= 3,
    'page-collector': save.totalPagesFound >= 100,
    'ten-runs': save.totalRuns >= 10,
    'daily-reader': save.dailyStreak >= 3,
    'page-hoarder': save.totalPagesFound >= 250,
  };
  const newlyEarned = ACHIEVEMENTS.filter((item) => checks[item.id] && !save.achievements.includes(item.id)).map((item) => item.id);
  save.achievements.push(...newlyEarned);
  persistSave(save);
  return { awarded: award, newlyEarned, firstClear: Boolean(firstStoryClear), dailyStreakBonus };
}

export function resetSave(): SaveData {
  const save = freshSave();
  persistSave(save);
  return save;
}
